import { defineStore } from 'pinia';
import Api from '@/http/api';
import Auth from '@/http/auth';

type AppLocale = 'en' | 'nl';
type AuthUser = {
  language?: string;
  // The user payload is consumed dynamically across existing Vue components.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
} | null;

const defaultLocale: AppLocale = 'nl';

function resolveStoredLocale(): AppLocale {
  if (typeof window === 'undefined') {
    return defaultLocale;
  }

  return normalizeLocale(window.localStorage?.getItem('app.locale'));
}

function normalizeLocale(value?: string | null): AppLocale {
  return value === 'en' ? 'en' : defaultLocale;
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as AuthUser,
    locale: resolveStoredLocale(),
  }),
  actions: {
    set_user(user: AuthUser) {
      this.user = user ? {
        ...user,
        language: this.locale,
      } : null;

      if (typeof window !== 'undefined' && !window.localStorage?.getItem('app.locale') && user?.language) {
        this.set_locale(user.language);
      }
    },
    set_locale(locale: string) {
      this.locale = normalizeLocale(locale);

      if (typeof window !== 'undefined') {
        window.localStorage?.setItem('app.locale', this.locale);
        document.documentElement.setAttribute('lang', this.locale);
      }

      if (this.user) {
        this.user = {
          ...this.user,
          language: this.locale,
        };
      }
    },
    async attempt_user() {
      return Api.getCurrentUser()
        .then((response) => {
          this.set_user(response.data);
          return response;
        })
        .catch((error) => {
          this.user = null;
          throw error;
        });
    },
    async logout() {
      return Auth.logout()
        .then(() => {
          this.$reset();
        })
        .catch((error) => {
          console.error(error);
          throw error;
        });
    },
  },
  getters: {
    currentUser: (state) => state.user,
    currentLocale: (state) => state.locale,
  },
});
